import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// Connects to data-controller="task-generation"
export default class extends Controller {
  static targets = ["progressBanner", "progressBar", "statusText", "stepText",
                    "percentageText", "phasesCompleted", "totalPhases", "storiesCompleted", "startButton"]

  static values = {
    projectId: String,
    statusUrl: String
  }

  connect() {
    console.log('[StoryGeneration] Controller connected')
    this.isGenerating = false
    this.generationCompleted = false // Track if generation has completed to prevent re-showing banner
    this.subscription = null
    this.lastRefreshTime = 0
    this.refreshThrottleMs = 2000 // Only refresh table every 2 seconds max
    this.storyCount = 0 // Track number of stories to detect first story creation

    // Check for ongoing generation immediately
    this.checkForOngoingGeneration()
  }

  disconnect() {
    console.log('[StoryGeneration] Controller disconnecting')
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  async checkForOngoingGeneration() {
    // Don't check if we've already marked generation as completed
    if (this.generationCompleted) {
      console.log('[StoryGeneration] Generation already completed, skipping status check')
      return
    }

    console.log('[StoryGeneration] Checking for ongoing generation...')

    // Poll with retry to handle race condition where page reloads before job starts
    // Try 10 times with 500ms intervals (5 seconds total)
    const maxAttempts = 10
    const pollInterval = 500 // ms

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await fetch(this.statusUrlValue, {
          headers: {'Accept': 'application/json'}
        })

        console.log(`[StoryGeneration] Status check attempt ${attempt}/${maxAttempts}: ${response.status}`)

        if (response.ok) {
          const data = await response.json()

          if (data.in_progress && !this.generationCompleted) {
            console.log('[StoryGeneration] Generation in progress, showing progress banner')
            this.showProgressBanner()
            this.setupGenerationChannel()
            this.isGenerating = true

            // Update with current progress
            this.updateProgress(data)
            return // Found it, stop polling
          } else if (attempt < maxAttempts) {
            // Not in progress yet, but we have more attempts - wait and retry
            console.log(`[StoryGeneration] Not in progress yet, retrying in ${pollInterval}ms...`)
            await new Promise(resolve => setTimeout(resolve, pollInterval))
            continue
          } else {
            // Final attempt and still no generation
            console.log('[StoryGeneration] No generation in progress after all retries')
            this.hideProgressBanner()
          }
        }
      } catch (error) {
        console.error('[StoryGeneration] Error checking generation status:', error)
        // On error, wait and retry if we have attempts left
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, pollInterval))
        }
      }
    }
  }

  startGeneration(event) {
    event.preventDefault()
    console.log('[StoryGeneration] Start generation clicked')

    // Show progress banner immediately
    this.showProgressBanner()
    this.updateStatusText('Generating User Stories...', 'Initializing...')

    // Setup channel before submitting form
    this.setupGenerationChannel()
    this.isGenerating = true

    // Submit the form
    const form = event.target.closest('form')
    if (form) {
      console.log('[StoryGeneration] Submitting form')
      form.requestSubmit()
    }
  }

  setupGenerationChannel() {
    if (this.subscription) {
      console.log('[StoryGeneration] Channel already subscribed')
      return
    }

    console.log('[StoryGeneration] Setting up generation channel for project:', this.projectIdValue)

    this.subscription = consumer.subscriptions.create(
      {
        channel: "PhasedGenerationChannel",
        project_id: this.projectIdValue
      },
      {
        connected: () => {
          console.log('[StoryGeneration] WebSocket connected')
        },

        disconnected: () => {
          console.log('[StoryGeneration] WebSocket disconnected')
        },

        received: (data) => {
          console.log('[StoryGeneration] Broadcast received:', data)
          this.handleGenerationUpdate(data)
        }
      }
    )
  }

  handleGenerationUpdate(data) {
    console.log('[StoryGeneration] Received broadcast:', {
      type: data.type,
      status: data.status,
      message: data.message,
      dataKeys: Object.keys(data),
      fullData: data
    })

    switch(data.type || data.status) {
      case 'phase_started':
        const formattedPhaseName = this.formatPhaseName(data.phase_name)
        this.updateStatusText(`Generating ${formattedPhaseName} Stories...`)

        if (data.total_phases) this.totalPhasesTarget.textContent = data.total_phases
        if (data.current_phase) this.phasesCompletedTarget.textContent = data.current_phase - 1

        if (data.percentage !== undefined) {
          const percent = Math.min(100, Math.round(data.percentage))
          this.progressBarTarget.value = percent
          this.percentageTextTarget.textContent = `${percent}%`
        }
        break

      case 'story_created':
        console.log('[StoryGeneration] Story created event received:', {
          story_id: data.story_id,
          story_title: data.story_title,
          phase: data.phase_name,
          count: data.story_count
        })

        if (data.message && this.hasStepTextTarget) {
          this.stepTextTarget.textContent = data.message
        }

        // Update story count immediately when a story is created
        if (data.story_count && this.hasStoriesCompletedTarget) {
          this.storiesCompletedTarget.textContent = data.story_count
        }

        // Check if this is the first story (transition from empty state to table)
        const isFirstStory = this.storyCount === 0 && data.story_count > 0
        if (data.story_count) {
          this.storyCount = data.story_count
        }

        // Force immediate refresh for first story to transition from empty state
        // Otherwise use normal throttled refresh
        console.log('[StoryGeneration] Triggering table refresh for new story', isFirstStory ? '(FIRST STORY - FORCING)' : '(throttled)')
        this.refreshStoriesTable(isFirstStory)
        break

      case 'phase_completed':
        const percent = Math.min(100, Math.round(data.percentage || 0))
        const completedPhaseName = this.formatPhaseName(data.phase_name)

        this.progressBarTarget.value = percent
        this.percentageTextTarget.textContent = `${percent}%`

        if (data.current_phase) this.phasesCompletedTarget.textContent = data.current_phase
        if (data.completed_stories) this.storiesCompletedTarget.textContent = data.completed_stories
        if (this.hasStepTextTarget) {
          this.stepTextTarget.textContent = `✓ ${completedPhaseName} complete (${data.phase_story_count || 0} stories)`
        }

        // Force refresh the story table (bypass throttle for phase completion)
        this.refreshStoriesTable(true)
        break

      case 'generation_complete':
      case 'completed':
      case 'sprint_zero_story_generation_completed':
      case 'sprint_zero_story_generation_complete':
        console.log('[StoryGeneration] ✓ Generation completed!')

        // Mark as completed to prevent re-showing banner
        this.generationCompleted = true
        this.isGenerating = false

        this.updateStatusText('Generation Complete!', data.message || 'All user stories generated successfully')
        this.progressBarTarget.value = 100
        this.percentageTextTarget.textContent = '100%'

        if (data.completed_phases || data.phases_completed) {
          this.phasesCompletedTarget.textContent = data.completed_phases || data.phases_completed
        }
        if (data.completed_stories || data.story_count) {
          this.storiesCompletedTarget.textContent = data.completed_stories || data.story_count
        }

        // Show success toast notification immediately
        this.showSuccessToast(data.total_stories || data.completed_stories || 0, data.completed_phases || 1)

        // Unsubscribe from channel and clean up
        if (this.subscription) {
          this.subscription.unsubscribe()
          this.subscription = null
        }

        // Refresh the stories table one final time
        this.refreshStoriesTable(true)

        // Hide the progress banner after a brief moment to show 100% completion
        console.log('[StoryGeneration] Hiding progress banner in 1.5s...')
        setTimeout(() => {
          this.hideProgressBanner()
          console.log('[StoryGeneration] Progress banner hidden, reloading in 0.5s...')

          // Reload the page to show final state
          setTimeout(() => {
            console.log('[StoryGeneration] Reloading page now...')
            window.location.reload()
          }, 500)
        }, 1500)
        break

      case 'generation_error':
      case 'error':
        this.updateStatusText('Generation Failed', data.error || 'An error occurred')
        if (this.hasStepTextTarget) {
          this.stepTextTarget.classList.add('text-error')
        }

        setTimeout(() => {
          this.hideProgressBanner()
        }, 5000)
        break
    }
  }

  updateProgress(data) {
    if (data.completed_phases !== undefined && this.hasPhasesCompletedTarget) {
      this.phasesCompletedTarget.textContent = data.completed_phases
    }
    if (data.total_phases !== undefined && this.hasTotalPhasesTarget) {
      this.totalPhasesTarget.textContent = data.total_phases
    }
    if (data.completed_stories !== undefined && this.hasStoriesCompletedTarget) {
      this.storiesCompletedTarget.textContent = data.completed_stories
    }
    if (data.percentage !== undefined && this.hasProgressBarTarget) {
      this.progressBarTarget.value = data.percentage
      this.percentageTextTarget.textContent = `${data.percentage}%`
    }

    // Set better initial message
    this.updateStatusText('Generating User Stories...', 'Generation in progress...')
  }

  updateStatusText(status, step = null) {
    if (this.hasStatusTextTarget) this.statusTextTarget.textContent = status
    if (step && this.hasStepTextTarget) this.stepTextTarget.textContent = step
  }

  showProgressBanner() {
    if (this.hasProgressBannerTarget) {
      this.progressBannerTarget.classList.remove('hidden')
    }
  }

  hideProgressBanner() {
    if (this.hasProgressBannerTarget) {
      this.progressBannerTarget.classList.add('hidden')
    }
  }

  showSuccessToast(totalStories, totalPhases) {
    console.log('[StoryGeneration] Showing success toast notification')

    const toast = document.createElement('div')
    toast.className = 'toast toast-end z-50'
    toast.style.cssText = 'position: fixed; bottom: 1rem; right: 1rem;'

    const storiesText = totalStories === 1 ? '1 story' : `${totalStories} stories`
    const phasesText = totalPhases === 1 ? '1 phase' : `${totalPhases} phases`

    toast.innerHTML = `
      <div class="alert alert-success shadow-lg max-w-sm cursor-pointer animate-bounce-in"
           onclick="this.parentNode.remove()">
        <div class="flex">
          <span class="text-2xl">✅</span>
          <div class="flex-1 ml-3">
            <div class="font-semibold text-lg">Story Generation Complete!</div>
            <div class="text-sm opacity-90 mt-1">
              Generated ${storiesText} across ${phasesText}
            </div>
          </div>
          <button class="btn btn-ghost btn-xs" onclick="event.stopPropagation(); this.closest('.toast').remove()">
            ✕
          </button>
        </div>
      </div>
    `

    document.body.appendChild(toast)
    console.log('[StoryGeneration] Toast notification displayed')

    // Auto-remove after 8 seconds (gives user time to see it before page reload)
    setTimeout(() => {
      if (toast.parentNode) {
        toast.remove()
      }
    }, 8000)
  }

  formatPhaseName(phaseName) {
    const phaseNames = {
      'data_model': 'Data Model',
      'controllers_services': 'Controllers & Services',
      'view_layouts': 'View Layouts',
      'view_pages': 'View Pages',
      'email_notifications': 'Email Notifications',
      'seed_data': 'Seed Data',
      'browser_testing_final': 'Browser Testing'
    }
    return phaseNames[phaseName] || phaseName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  refreshStoriesTable(force = false) {
    // Throttle refreshes to prevent excessive AJAX calls
    const now = Date.now()
    const timeSinceLastRefresh = now - this.lastRefreshTime

    if (!force && timeSinceLastRefresh < this.refreshThrottleMs) {
      console.log(`[StoryGeneration] Throttling refresh (${timeSinceLastRefresh}ms since last refresh)`)
      return
    }

    this.lastRefreshTime = now
    console.log('[StoryGeneration] Refreshing stories table...')

    // Check if table exists - if not, we're in empty state and need to reload
    const tbody = document.querySelector('#stories-table tbody')
    if (!tbody) {
      console.log('[StoryGeneration] Table not found (empty state), reloading page to show stories...')
      window.location.reload()
      return
    }

    // Get current sort parameter from URL or dropdown
    const sortFilter = document.getElementById('sort-filter')
    const currentSort = sortFilter ? sortFilter.value : 'recently_updated'

    // Build proper URL for creator module endpoint
    // The current page URL contains the project ID or slug
    // We need to construct the correct module URL
    const currentPath = window.location.pathname
    const pathMatch = currentPath.match(/\/creator\/projects\/([^\/]+)/)

    if (!pathMatch) {
      console.error('[StoryGeneration] Could not extract project ID/slug from current URL')
      return
    }

    const projectIdOrSlug = pathMatch[1]
    const baseUrl = `/creator/projects/${projectIdOrSlug}/module/user_stories`
    const url = new URL(baseUrl, window.location.origin)
    url.searchParams.set('sort', currentSort)
    url.searchParams.set('format', 'json')

    console.log('[StoryGeneration] Fetching stories from:', url.toString())

    // Fetch updated stories via AJAX
    fetch(url.toString(), {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(response => {
        console.log('[StoryGeneration] Response status:', response.status)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then(data => {
        console.log('[StoryGeneration] Received response:', {
          count: data.count,
          hasHtml: !!data.stories_html,
          htmlLength: data.stories_html ? data.stories_html.length : 0
        })

        // Update table with new stories
        if (data.stories_html) {
          const tbody = document.querySelector('#stories-table tbody')
          if (tbody) {
            const oldRowCount = tbody.querySelectorAll('tr').length
            tbody.innerHTML = data.stories_html
            const newRowCount = tbody.querySelectorAll('tr').length

            console.log('[StoryGeneration] Table updated:', {
              oldRows: oldRowCount,
              newRows: newRowCount,
              difference: newRowCount - oldRowCount
            })

            // Update visible count
            const visibleCount = document.getElementById('visible-count')
            if (visibleCount) {
              visibleCount.textContent = data.count
            }

            console.log('[StoryGeneration] ✅ Stories table updated successfully')
          } else {
            // Table disappeared between checks (shouldn't happen, but reload if it does)
            console.warn('[StoryGeneration] Stories table tbody disappeared, reloading...')
            window.location.reload()
          }
        } else {
          console.warn('[StoryGeneration] No stories_html in response')
        }
      })
      .catch(error => {
        console.error('[StoryGeneration] ❌ Error refreshing stories table:', error)
      })
  }
}
